import React from 'react';
import './Calculator.css';
import CalculatorHead from './CalculatorHead';
import CalculatorSliders from './CalculatorSliders';
import CalculatorResults from './CalculatorResults';
import { debounce } from 'lodash';
import Interested from '../common/components/interested/Interested';
import { API } from 'aws-amplify';
import Modal from "react-modal";
import { useScrollBlock } from '../common/components/UseScrollBlock';

function Calculator() {
  const DEFAULT_CREDIT_SCORE = '580-659';
  const DEFAULT_DEBT = 25000;
  const DEFAULT_DEBT_APR = 22;
  const DEFAULT_DEBT_CONSOLIDATION_APR = 15;
  const DEFAULT_SERA_APR = 15;
  const DEFAULT_MONTHLY_INCOME = 5000;
  const DEFAULT_MONTHLY_NON_CC_EXPENSES = 2000;
  const DEFAULT_MONTHLY_PAYMENT = 800;
  const DEFAULT_MONTHLY_SAVINGS = 100;
  const DEFAULT_LOAN_APR = 12;

  const creditScoreRef = React.useRef(DEFAULT_CREDIT_SCORE);
  const debtRef = React.useRef();
  const debtAPRRef = React.useRef();
  const debtConsolidationAPRRef = React.useRef();
  const seraAPRRef = React.useRef();
  const monthlyIncomeRef = React.useRef();
  const monthlyPaymentRef = React.useRef();
  const monthlySavingsRef = React.useRef();
  const hasInputErrorRef = React.useRef(false);
  
  const [withSeraAmount, setWithSeraAmount] = React.useState(3300);
  const [withSeraMonths, setWithSeraMonths] = React.useState(19);
  const [withConsolidationMonths, setWithConsolidationMonths] = React.useState(44);
  const [withConsolidationAmount, setWithConsolidationAmount] = React.useState(7300);
  const [withoutHelpMonths, setWithoutHelpMonths] = React.useState(52);
  const [withoutHelpAmount, setWithoutHelpAmount] = React.useState(14400);
  const [creditScore, setCreditScore] = React.useState(DEFAULT_CREDIT_SCORE);
  const [minimumMonthlyPayment, setMinimumMonthlyPayment] = React.useState(DEFAULT_MONTHLY_PAYMENT);
  const [monthlyPayment, setMonthlyPayment] = React.useState(DEFAULT_MONTHLY_PAYMENT);
  const [hasMinimumPaymentError, setHasMinimumPaymentError] = React.useState(false);
  const [loanAPR, setLoanAPR] = React.useState(DEFAULT_LOAN_APR);

  const [errorMsg, setErrorMsg] = React.useState('');

  const [slidersPaneOpen, setSlidersPaneOpen] = React.useState(false);

  const [loading, setLoading] = React.useState(false);

  const [currentDebtValue, setCurrentDebtValue] = React.useState(25000);
  const [currentMonthlyIncomeValue, setCurrentMonthlyIncomeValue] = React.useState(5000);
  const [currentMonthlyPaymentValue, setCurrentMonthlyPaymentValue] = React.useState(800);
  const [currentMonthlySavingsValue, setCurrentMonthlySavingsValue] = React.useState(100);
  const [isModalButtonDisabled, setIsModalButtonDisabled] = React.useState(false);

  const [blockScroll, allowScroll] = useScrollBlock();

  const useEstimatesContainerRef = React.useRef();

  const getMinMonthlyPayment = (debtBalance, debtAPR) => {
    const yearlyInterest = debtBalance * (debtAPR + 0.1);
    const monthlyInterest = yearlyInterest / 12;
    const roundedMonthlyInterest = Math.ceil(monthlyInterest / 100) * 100;
    return Math.round(roundedMonthlyInterest);
  }

  const sliderOnChange = () => {
    setIsModalButtonDisabled(hasInputErrorRef.current === true);
    setMinimumMonthlyPayment(getMinMonthlyPayment(parseInt(debtRef.current.value), 0.22));
    if (!slidersPaneOpen) {
      if (debtRef.current && monthlyIncomeRef.current && monthlyPaymentRef.current && monthlySavingsRef.current) {
        fetchCalculation(parseInt(debtRef.current.value), parseInt(monthlyIncomeRef.current.value), parseInt(monthlyPaymentRef.current.value), parseInt(monthlySavingsRef.current.value));
      }
    }
  }

  const handleEstimatesButtonClick = () => {
    blockScroll();
    setSlidersPaneOpen(true);
  }

  const handleUpdateButtonClick = () => {
    fetchCalculation(parseInt(debtRef.current.value), parseInt(monthlyIncomeRef.current.value), parseInt(monthlyPaymentRef.current.value), parseInt(monthlySavingsRef.current.value));
    setCurrentDebtValue(parseInt(debtRef.current.value));
    setCurrentMonthlyIncomeValue(parseInt(monthlyIncomeRef.current.value));
    setCurrentMonthlyPaymentValue(parseInt(monthlyPaymentRef.current.value));
    setCurrentMonthlySavingsValue(parseInt(monthlySavingsRef.current.value));
    closeModal();
  }

  const closeModal = () => {
    allowScroll();
    setSlidersPaneOpen(false);
    setIsModalButtonDisabled(false);
    hasInputErrorRef.current = false;
  }

  const fetchCalculation = React.useCallback(
    debounce((debt, monthlyIncome, monthlyPayment, monthlySavings) => doCalculation(debt, monthlyIncome, monthlyPayment, monthlySavings), 500), []
  );

  const doCalculation = (debt, monthlyIncome, monthlyPayment, monthlySavings) => {
    if (loading) return;
    console.log(typeof(debt));
    console.log('calling calculator API: debt - ' + debt + ', monthlyIncome - ' + monthlyIncome + 
      ', monthlyPayment - ' + monthlyPayment + ', monthlySavings - ' + monthlySavings);

    setCreditScore(creditScoreRef.current);
    setMonthlyPayment(parseInt(monthlyPaymentRef.current.value));
    setHasMinimumPaymentError(hasInputErrorRef.current);      
    
    if (hasInputErrorRef.current === true) {
      console.log('input error');
      return;
    }

    setLoading(true);
    // Call API
    API.post('waitlistAPI', '/calculatorSubmissions', {
      body: {
        debtBalance: debt,
        debtApr: 0.22,
        monthlyIncome: monthlyIncome,
        monthlyNonCCExpenses: Math.ceil(monthlyIncome * 0.3),
        monthlyPayment: monthlyPayment,
        monthlySavings: monthlySavings,
        creditScoreRange: creditScoreRef.current
      }
    }).then ((resp) => {
      console.log(resp);
      setWithoutHelpMonths(resp.item.numPaymentsStandard);
      setWithoutHelpAmount(resp.item.totalInterestPaidStandard);
      setWithConsolidationMonths(resp.item.numPaymentsDebtConsolidation);
      setWithConsolidationAmount(resp.item.totalInterestPaidDebtConsolidation);
      setWithSeraMonths(resp.item.numPaymentsAdr);
      setWithSeraAmount(resp.item.totalInterestPaidAdr);
      setLoanAPR(resp.item.loanApr * 100);
    })
    setLoading(false);
  }

  React.useEffect(() => {
    fetchCalculation(DEFAULT_DEBT, DEFAULT_MONTHLY_INCOME, DEFAULT_MONTHLY_PAYMENT, DEFAULT_MONTHLY_SAVINGS);
  }, [fetchCalculation]);

  const handleScroll = React.useCallback((event) => { 
    if (typeof window === "undefined" || !useEstimatesContainerRef.current) {
      return;
    }
    useEstimatesContainerRef.current.style.display = (window.scrollY > 950) ? 'none' : 'block';
    useEstimatesContainerRef.current.style.opacity = (window.scrollY > 200 && window.scrollY < 700) ? 1 : 0;
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    handleScroll();
    window.addEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <React.Fragment>
      <CalculatorHead />
      <div className='calculator'>
        
        <div className='calculator-header-container'>
          <p className='title'>Debt payoff calculator</p>
          <p>
          Discover how much time and money Sera can help you save repaying your debt. Note that low credit scores may limited access to certain Sera tools. However, Sera helps build credit quickly, enabling more options for you in the future.
          </p>
        </div>
        <div className='calculator-container'>
          <CalculatorSliders creditScoreRef={creditScoreRef} debtRef={debtRef} debtAPRRef={debtAPRRef} debtConsolidationAPRRef={debtConsolidationAPRRef} seraAPRRef={seraAPRRef} monthlyIncomeRef={monthlyIncomeRef} monthlyPaymentRef={monthlyPaymentRef} monthlySavingsRef={monthlySavingsRef} hasInputErrorRef={hasInputErrorRef} onChange={sliderOnChange} errorMsg={errorMsg} defaultDebtValue={currentDebtValue} defaultDebtAPRValue={DEFAULT_DEBT_APR} defaultDebtConsolidationAPRValue={DEFAULT_DEBT_CONSOLIDATION_APR} defaultSeraAPRValue={DEFAULT_SERA_APR} defaultMonthlyIncomeValue={currentMonthlyIncomeValue} defaultMonthlyPaymentValue={currentMonthlyPaymentValue} defaultMonthlySavingsValue={currentMonthlySavingsValue} loanAPR={loanAPR} />
          
          <CalculatorResults isAdvanced={false} withConsolidationAmount={withConsolidationAmount} withConsolidationMonths={withConsolidationMonths} withoutHelpAmount={withoutHelpAmount} withoutHelpMonths={withoutHelpMonths} withSeraAmount={withSeraAmount} withSeraMonths={withSeraMonths} errorMsg={errorMsg} handleEstimatesButtonClick={handleEstimatesButtonClick} creditScore={creditScore} monthlyPayment={monthlyPayment} minimumMonthlyPayment={minimumMonthlyPayment} hasMinimumPaymentError={hasMinimumPaymentError} loanAPR={loanAPR}/>

        </div>

        <Interested showGraphic={false} />

        <div ref={useEstimatesContainerRef} className='use-estimates-container'>
          <button className='main' onClick={handleEstimatesButtonClick}>Use your own estimates</button>
        </div>

        <Modal
          isOpen={slidersPaneOpen}
          onRequestClose={closeModal}
          contentLabel="My dialog"
          className="calculator-modal"
          overlayClassName="modal-background"
          closeTimeoutMS={3000}
          >
          <div className='calculator-modal-container' >
            <div className='modal-close-btn'
              onClick={closeModal} />
            <CalculatorSliders creditScoreRef={creditScoreRef} debtRef={debtRef} debtAPRRef={debtAPRRef} debtConsolidationAPRRef={debtConsolidationAPRRef} seraAPRRef={seraAPRRef} monthlyIncomeRef={monthlyIncomeRef} monthlyPaymentRef={monthlyPaymentRef} monthlySavingsRef={monthlySavingsRef} hasInputErrorRef={hasInputErrorRef} onChange={sliderOnChange} errorMsg={errorMsg} defaultDebtValue={currentDebtValue} defaultDebtAPRValue={DEFAULT_DEBT_APR} defaultDebtConsolidationAPRValue={DEFAULT_DEBT_CONSOLIDATION_APR} defaultSeraAPRValue={DEFAULT_SERA_APR} defaultMonthlyIncomeValue={currentMonthlyIncomeValue} defaultMonthlyPaymentValue={currentMonthlyPaymentValue} defaultMonthlySavingsValue={currentMonthlySavingsValue} loanAPR={loanAPR} />

            <div style={{width: "100%", textAlign: "center"}}>
              <button className='main' disabled={isModalButtonDisabled} onClick={handleUpdateButtonClick}>Apply changes</button>
            </div>
          </div>
        </Modal>
      </div>
    </React.Fragment>
  );
}

export default Calculator;